import { useThemeValue } from '@/lib/theme';
import { cn } from '@/lib/utils';

/**
 * Official Fleetwise AI brand mark — Brand Guidelines v1.0.
 *
 * The brand kit ships two hand-tuned variants of the mark: one tuned for Ink
 * (dark) surfaces and one for Mist (light). We theme-swap between those two
 * official files rather than recoloring a single asset — the brand book is
 * explicit: NEVER recolor, stretch, rotate, or add effects.
 *
 * The SVGs are served verbatim from `client/public/brand/`, so what renders is
 * byte-identical to the brand kit. The mark's artboard is 100x92, so height is
 * derived from width to preserve aspect (stretching is a brand violation).
 *
 * MIN_SIZE enforces the brand book's 24px on-screen floor for the symbol.
 *
 * NOTE: the app icon (`favicon-*.png` / `fleetwise-appicon.svg`) is a rounded
 * Ink *tile* intended for app stores, PWA install and the browser tab — it is
 * NOT the in-app mark. Using it as in-app chrome (as the pre-v1.0 build did)
 * puts a dark tile on light-theme surfaces. Use BrandSymbol / BrandLockup here.
 */

const MIN_SIZE = 24;
const ARTBOARD_RATIO = 92 / 100;

interface BrandSymbolProps {
  /** Rendered width in px. Clamped to the brand's 24px minimum. */
  size?: number;
  /** Accessible name. Leave empty when an adjacent wordmark already names it. */
  alt?: string;
  className?: string;
}

export function BrandSymbol({ size = 28, alt = '', className }: BrandSymbolProps) {
  // Read-only: the mark reacts to the theme but must not *require* the provider,
  // so a layout can still be mounted in isolation (e.g. a component test).
  const theme = useThemeValue();
  const width = Math.max(size, MIN_SIZE);
  const height = Math.round(width * ARTBOARD_RATIO);
  const src =
    theme === 'light' ? '/brand/fleetwise-symbol-light.svg' : '/brand/fleetwise-symbol-dark.svg';

  return (
    <img
      src={src}
      alt={alt}
      aria-hidden={alt ? undefined : true}
      width={width}
      height={height}
      className={cn('shrink-0 select-none', className)}
    />
  );
}

interface BrandLockupProps {
  /** Symbol width in px; the wordmark scales alongside it. */
  size?: number;
  className?: string;
  /**
   * Extra classes for the TEXT half of the lockup only — lets a cramped host
   * (the tech header at phone width) collapse to symbol-only while every
   * other consumer keeps the full mark. The symbol always renders.
   */
  textClassName?: string;
}

/**
 * Mark + "Fleetwise AI" wordmark.
 *
 * The wordmark is real HTML text (not the lockup SVG's baked-in text) on
 * purpose: an SVG rendered through <img> cannot load the Plus Jakarta Sans
 * webfont and would silently fall back to Helvetica/Arial. Rendering it as
 * text gives us the true brand face, correct theme color, and real
 * selectable/accessible text.
 *
 * Type spec mirrors the official lockup: Plus Jakarta Sans ExtraBold (800),
 * tight tracking, "AI" set in Signal Cyan.
 */
export function BrandLockup({ size = 28, className, textClassName }: BrandLockupProps) {
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <BrandSymbol size={size} />
      {/* A brand mark never wraps — compressed headers were stacking
          "Fleetwise" / "AI" on two lines (elite-audit F12, 2026-08-26). */}
      <span
        className={cn(
          'text-fw-text text-base font-extrabold tracking-[-0.03em] whitespace-nowrap',
          textClassName,
        )}
      >
        Fleetwise <span className="text-fw-accent">AI</span>
      </span>
    </span>
  );
}
